/**
 * @file verification-code.schema.ts
 * @description Define o schema para um código de verificação temporário, usado para
 * confirmar a conta, alterar a senha ou resetar a senha.
 *
 * A coleção 'verificationCodes' é projetada para ser de curta duração,
 * com documentos sendo deletados automaticamente via política de TTL do Firestore.
 */

import { z } from 'zod';

// --- Helper Schemas & Enums ---

/**
 * Define o propósito do código de verificação.
 * - ACCOUNT_CONFIRMATION: Para o cadastro inicial.
 * - PASSWORD_RESET: Para o fluxo de "esqueci minha senha".
 * - PASSWORD_CHANGE: Para confirmar uma alteração de senha de um usuário já logado.
 * - EMAIL_CHANGE: Para confirmar o novo e-mail de quem troca o próprio, já logado.
 */
const VerificationPurpose = z.enum([
  'ACCOUNT_CONFIRMATION',
  'PASSWORD_RESET',
  'PASSWORD_CHANGE',
  // Confirmar o e-mail da empresa no link validado (A062). Propósito próprio
  // para o código de um não valer pelo outro.
  'CORPORATE_EMAIL',
  // Confirmar o novo e-mail de quem troca o próprio em Meu Perfil (14/10/2026).
  'EMAIL_CHANGE',
]);


// --- Main VerificationCode Schema ---

/**
 * Define o schema para um documento na coleção 'verificationCodes'.
 */
const VerificationCodeSchema = z.object({
  // O email do usuário para o qual o código foi enviado.
  // Usamos email em vez de userId pois no fluxo de "esqueci a senha", não temos o userId.
  email: z.string().email(),
  
  // O código de verificação. IMPORTANTE: Este campo deve ser ARMAZENADO COM HASH no banco.
  code: z.string(),

  // O propósito deste código específico.
  purpose: VerificationPurpose,
  
  // A data e hora em que este código expira e se torna inválido.
  // Este campo será usado pela política de TTL do Firestore para exclusão automática.
  expiresAt: z.date(),

  // Campo de auditoria padrão.
  createdAt: z.date(),

  // Nº de tentativas de verificação ERRADAS deste código (proteção contra brute-force).
  // Ao atingir o limite, o código é invalidado e um novo precisa ser solicitado.
  attempts: z.number().int().nonnegative().default(0),

  // Timestamps (epoch ms) dos pedidos recentes de código para este email+purpose.
  // Alimenta o cooldown de reenvio e o limite de frequência (rate-limit).
  requestTimestamps: z.array(z.number()).default([]),
});


// --- Inferred TypeScript Types ---

type VerificationCode = z.infer<typeof VerificationCodeSchema>;
type VerificationPurpose = z.infer<typeof VerificationPurpose>;


// --- Public Exports ---

export { VerificationCodeSchema, VerificationPurpose };
export type { VerificationCode };